import {html} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {classMap} from 'lit/directives/class-map.js';
import {ifDefined} from 'lit/directives/if-defined.js';
import {FlywheelComponent} from '../../../lib/component.js';
import {NavEvents} from '#lit/types/events.js';
import styles from './style.scss';

// Consts
export const BRAND_COMPONENT_TAG = 'fly-brand';
export const BASE_IMG_URL = '/assets/brand_base.png';
export const BASE_ICON_URL = '/assets/brand_icon_base.png';
export const WHITE_IMG_URL = '/assets/brand_white.svg';
export const BRAND_DEFAULT_WIDTH = '180px';
export const CUSTOM_LOGO_DEFAULT_WIDTH = '145px';
export const BRAND_ICON_WIDTH = '48px';

// Enums
export enum BrandVariants {
  BASE = 'base',
  WHITE = 'white',
}

// Maps
export const variantImages = new Map([
  [BrandVariants.BASE, BASE_IMG_URL],
  [BrandVariants.WHITE, WHITE_IMG_URL],
]);

export const variantIcons = new Map([
  [BrandVariants.BASE, BASE_ICON_URL],
  [BrandVariants.WHITE, BASE_ICON_URL],
]);

/**
 * Brand -- Display the flywheel brand
 */
@customElement(BRAND_COMPONENT_TAG)
export default class FlyBrand extends FlywheelComponent {
  @property()
  variant = BrandVariants.BASE;

  @property()
  width = BRAND_DEFAULT_WIDTH;

  @property({type: Boolean})
  icon = false;

  @property({type: Boolean})
  responsive = false;

  @property({type: String})
  customLogo = '';

  @property({type: String})
  customIcon = '';

  private static readonly BRAND_ALT_TEXT = 'Growthloop spinning wheel icon';

  private _memoizedLogo?: string;
  private _memoizedWidth?: string;

  get orgLogo() {
    return this.customLogo || '';
  }

  get orgIcon() {
    return this.customIcon || '';
  }

  get img() {
    if (this._memoizedLogo) return this._memoizedLogo;

    if (this.orgLogo !== '') {
      this._memoizedLogo = this.orgLogo;
      return this._memoizedLogo;
    }

    this._memoizedLogo = this.icon
      ? variantIcons.get(this.variant)
      : variantImages.get(this.variant);
    return this._memoizedLogo;
  }

  private get computedWidth() {
    if (this._memoizedWidth) return this._memoizedWidth;

    this._memoizedWidth = this.orgLogo
      ? CUSTOM_LOGO_DEFAULT_WIDTH
      : BRAND_DEFAULT_WIDTH;
    return this._memoizedWidth;
  }

  static get styles() {
    return [styles];
  }

  load() {
    if (this.responsive) {
      window.addEventListener(NavEvents.TOGGLE_COLLAPSE, event => {
        const {collapsed} = event.detail;
        this.icon = collapsed;
      });
    }
  }

  render() {
    const logo = this.img;
    const responsiveLogo = this.orgLogo || variantImages.get(this.variant);
    const icon = this.orgIcon || variantIcons.get(this.variant);

    return html`
      <div
        class="brand ${classMap({'brand--icon': this.icon})}"
        style="padding-left:2px; padding-top:10px;"
      >
        ${!this.responsive
          ? html`<img
              alt="${FlyBrand.BRAND_ALT_TEXT}"
              src="${ifDefined(logo)}"
              width="${this.computedWidth}"
              loading="lazy"
            />`
          : html`
              <div class="brand__full">
                <img
                  alt="${FlyBrand.BRAND_ALT_TEXT}"
                  src="${ifDefined(responsiveLogo)}"
                  width="${this.computedWidth}"
                  loading="lazy"
                />
              </div>
              <div class="brand__icon">
                <img
                  alt="${FlyBrand.BRAND_ALT_TEXT}"
                  src="${ifDefined(icon)}"
                  width="${BRAND_ICON_WIDTH}"
                  loading="lazy"
                />
              </div>
            `}
      </div>
    `;
  }
}

FlywheelComponent.tagRegistry.add(BRAND_COMPONENT_TAG);

declare global {
  interface HTMLElementTagNameMap {
    'fly-brand': FlyBrand;
  }
}
